import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReservationForm from '../components/ReservationForm';
import { getRestaurantById } from '../services/restaurantService';
import { getEventById } from '../services/eventService';
import { getShiftById } from '../services/shiftService';
import './ReservationWidget.css';

/**
 * ReservationWidget - Standalone widget page for embedding in external websites via iframe
 * This page contains only the reservation form without navbar, footer, or any other UI elements
 * Query params:
 * - restaurantId: The ID of the restaurant (required)
 */
const ReservationWidget = () => {
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEventDate, setIsEventDate] = useState(false);
  const [eventHouseRules, setEventHouseRules] = useState('');
  const [initialEvent, setInitialEvent] = useState(null);
  const [eventAccessDenied, setEventAccessDenied] = useState(null);
  // initialEventShift is consumed once by the form for pre-selection (then cleared);
  // displayEventShift is the persistent banner data shown in the widget UI.
  const [initialEventShift, setInitialEventShift] = useState(null);
  const [displayEventShift, setDisplayEventShift] = useState(null);

  const restaurantId = searchParams.get('restaurantId');
  const eventId = searchParams.get('eventId');
  const shiftId = searchParams.get('shiftId');

  useEffect(() => {
    if (!restaurantId) {
      setError('Restaurant ID is required');
      setLoading(false);
      return;
    }

    const loadRestaurant = async () => {
      try {
        setLoading(true);
        const data = await getRestaurantById(restaurantId);
        setRestaurant(data);
        setError(null);
      } catch (err) {
        console.error('Error loading restaurant:', err);
        setError('Failed to load restaurant information');
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [restaurantId]);

  // Fetch event if eventId is provided in URL
  useEffect(() => {
    if (!eventId) return;
    const loadEvent = async () => {
      try {
        const data = await getEventById(eventId);
        if (data.accessDenied) {
          setEventAccessDenied(data.message || 'This is a private show. You don\'t have access.');
          setInitialEvent(null);
          return;
        }
        setEventAccessDenied(null);
        if (data.event) {
          setInitialEvent(data.event);
        }
      } catch (err) {
        console.error('Error loading event:', err);
      }
    };
    loadEvent();
  }, [eventId]);

  // Fetch event shift if shiftId is provided in URL
  useEffect(() => {
    if (!shiftId) return;
    const loadShift = async () => {
      try {
        const data = await getShiftById(shiftId);
        if (data) {
          setInitialEventShift(data);
          setDisplayEventShift(data);
        }
      } catch (err) {
        console.error('Error loading shift:', err);
      }
    };
    loadShift();
  }, [shiftId]);

  if (!restaurantId) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">
          Missing restaurant ID. Please provide a restaurantId in the URL query parameter.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="widget-loading-container">
        <div className="widget-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">{error}</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">Restaurant not found</p>
      </div>
    );
  }

  if (eventAccessDenied) {
    const isLoggedIn = !!localStorage.getItem('tali_client_token');
    return (
      <div className="widget-error-container" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
        <div style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          color: '#6f42c1',
        }}>
          <i className="bi bi-lock-fill"></i>
        </div>
        <h3 style={{ marginBottom: '0.75rem' }}>Private Show</h3>
        <p className="widget-error-message" style={{ maxWidth: 480, margin: '0 auto 1rem' }}>
          {eventAccessDenied}
        </p>
        {!isLoggedIn && (
          <a
            href={`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="btn btn-primary"
            style={{ marginTop: '0.5rem' }}
          >
            Sign in to check access
          </a>
        )}
      </div>
    );
  }

  // Build custom color styles from restaurant settings
  const customColorStyles = {
    '--primary-purple': restaurant.widgetPrimaryColor || '#1A1A1A',
    '--secondary-purple': restaurant.widgetSecondaryColor || '#2D2D2D',
    '--accent-lime': restaurant.widgetAccentColor || '#BFFF00',
    '--text-dark': restaurant.widgetTextColor || '#1a1a1a',
    '--white': restaurant.widgetBackgroundColor || '#ffffff',
    // Derived colors for consistency
    '--light-purple': restaurant.widgetSecondaryColor || '#333333',
    '--bg-light': restaurant.widgetBackgroundColor || '#f9f9f9',
  };

  return (
    <div className="reservation-widget-wrapper" style={customColorStyles}>
      {/* Restaurant Image Header */}
      {restaurant.imageUrl && (
        <div className="widget-restaurant-header">
          <div className="widget-restaurant-image-wrapper">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${restaurant.imageUrl}`}
              alt={restaurant.name}
              className="widget-restaurant-image"
            />
            {(restaurant.location || (restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0)) && (
              <div className="widget-restaurant-overlay">
                {restaurant.location && (
                  <p className="widget-restaurant-location">
                    <i className="bi bi-geo-alt-fill"></i> {restaurant.location}
                  </p>
                )}
                {restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0 && (
                  <div className="widget-restaurant-contacts">
                    {restaurant.phoneNumbers.map((phone, index) => (
                      <a key={index} href={`tel:${phone}`} className="widget-restaurant-phone">
                        <i className="bi bi-telephone-fill"></i> {phone}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Widget Note */}
      {restaurant.widgetNote && restaurant.widgetNote.replace(/<[^>]*>/g, '').trim() && !showSuccess && !isEventDate && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: restaurant.widgetNote }}
          />
        </div>
      )}
      {/* Event House Rules - shown only on event dates */}
      {eventHouseRules && eventHouseRules.replace(/<[^>]*>/g, '').trim() && !showSuccess && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: eventHouseRules }}
          />
        </div>
      )}
      {/* Event Shift hero banner — image + description for shifts opened via copy-link */}
      {displayEventShift && displayEventShift.shiftType === 'event' && !showSuccess && (
        <div
          className="widget-event-shift-banner"
          style={{ maxWidth: 650, margin: '0 auto 1rem', padding: '0 1rem', width: '100%', boxSizing: 'border-box' }}
        >
          {displayEventShift.imageUrl && (
            <div
              style={{
                width: '100%',
                height: 220,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(26, 26, 26, 0.15)',
              }}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}${displayEventShift.imageUrl}`}
                alt={displayEventShift.label}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}
          <h4 style={{ marginTop: 12, marginBottom: 4 }}>{displayEventShift.label}</h4>
          {displayEventShift.description && (
            <p style={{ whiteSpace: 'pre-wrap', color: '#444', margin: 0 }}>{displayEventShift.description}</p>
          )}
        </div>
      )}
      <ReservationForm
        restaurantId={restaurantId}
        restaurantName={restaurant.name}
        restaurant={restaurant}
        isWidget={true}
        initialEvent={initialEvent}
        onInitialEventHandled={() => setInitialEvent(null)}
        initialEventShift={initialEventShift}
        onInitialEventShiftHandled={() => setInitialEventShift(null)}
        onSuccessChange={setShowSuccess}
        onEventDateChange={setIsEventDate}
        onEventHouseRulesChange={setEventHouseRules}
      />
    </div>
  );
};

export default ReservationWidget;
